import {MD5} from 'object-hash'
import type {
  TGameData,
  TGameId,
  TGamePosition,
  TPlayerId,
} from '@kapitalist/contract/types'
import * as NEL from '@kapitalist/utils/NEList'
import {safely} from '@kapitalist/utils/safely'
import {DataBackedModel} from '~/models/base'
import type GamePlayer from '~/models/game/GamePlayer'
import GamePosition, {
  PlayerGamePosition,
  SpectatorGamePosition,
} from '~/models/game/GamePosition'
import PastGamePosition from '~/models/game/PastGamePosition'

type PublicMembersFromApi = Pick<TGameData, 'gameId'>

const gamePositionDataToGamePosition =
  (gameData: TGameData, currentPlayerId: TPlayerId) =>
  (gamePosition: TGamePosition) =>
    GamePosition.build(gamePosition, gameData.players, currentPlayerId)

export default class GameData
  extends DataBackedModel<TGameData>
  implements PublicMembersFromApi
{
  readonly currentGamePosition: GamePosition
  readonly playerGamePosition: PlayerGamePosition | undefined
  readonly spectatorGamePosition: SpectatorGamePosition | undefined
  readonly gameId: TGameId
  readonly currentPlayerId: TPlayerId
  readonly pastGamePositions: NEL.NEList<PastGamePosition> | undefined
  readonly winner?: GamePlayer

  // MD5 of the API data, better than `currentGamePosition.time` for determining if anything
  // has changed.
  readonly hash: string

  static hash(data: TGameData): string {
    return MD5(data)
  }

  constructor(
    data: TGameData,
    currentPlayerId: TPlayerId,
    enableGameLog: boolean,
    precomputedHash?: string
  ) {
    super(data)
    this.hash = precomputedHash ?? GameData.hash(data)
    this.gameId = data.gameId
    this.currentPlayerId = currentPlayerId

    const createGamePosition = gamePositionDataToGamePosition(data, currentPlayerId)

    this.currentGamePosition = createGamePosition(data.currentGamePosition)
    this.playerGamePosition =
      this.currentGamePosition instanceof PlayerGamePosition
        ? this.currentGamePosition
        : undefined
    this.spectatorGamePosition =
      this.currentGamePosition instanceof SpectatorGamePosition
        ? this.currentGamePosition
        : undefined

    if (enableGameLog) {
      // NB: no longer sorting these here since we store them in a Map in Redux.
      // they need to be sorted when they are read from Redux instead
      this.pastGamePositions = NEL.createSafe(
        data.pastGamePositions.map(
          pgp => new PastGamePosition(pgp, data.players, currentPlayerId)
        )
      )
    } else {
      this.pastGamePositions = undefined
    }

    this.winner = safely(data.winCondition, wc =>
      this.currentGamePosition.players.find(p => p.id === wc.winnerId)
    )
  }
}
